import {
	matchHome,
	matchFabricEditor,
	matchSpaceOverview,
	matchViewPage,
	matchLivePage,
	matchAccessLink,
	matchCreateContent,
	matchWhiteboardPage,
	matchSearch,
} from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { resolveAccessLinkContent } from '@confluence/external-share/entry-points/resolveAccessLinkContent.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { waitUntilStatsigInitialized } from '@confluence/statsig-client/entry-points/getSessionDataAndInitializeStatsig.ts';

import {
	preloadAccessLinkViewPageComponents,
	preloadLivePagesComponents,
	preloadViewPageComponents,
	preloadSpaceOverview,
	preloadSearch,
	preloadNativeEditorComponentsPreloader,
	preloadCreateContent,
	preloadWhiteboardPage,
	preloadHome,
} from './preloaders/index.js';

/**
 * Preload components/chunks that are required for the critical path of the current route,
 * when they're not preloaded as part of SSR page delivery. This may happen when:
 *  - page is not SSR'ed due to some failure, or
 *  - no SSR enabled for the page
 *
 * Note: This function is called early, check waitUntilStatsigInitialized before using fg()
 */
export const preloadComponentsNoStatsigSupport = async (url, isTransition) => {
	const shouldPreload = isTransition || !window.__SSR_RENDERED__;

	let match;
	if ((match = matchHome(url))) {
		preloadHome(match);
	} else if (matchSpaceOverview(url)) {
		if (shouldPreload) {
			preloadSpaceOverview();
		}
	} else if (matchLivePage(url) && shouldPreload) {
		if (await waitUntilStatsigInitialized()) {
			preloadLivePagesComponents();
		}
	} else if ((match = matchAccessLink(url)) && shouldPreload) {
		// Neither `matchLivePage` nor `matchViewPage` can match here: they need a VIEW_PAGE match plus
		// cached page data, and an access-link URL carries only a token. So the chunks have to be
		// resolved explicitly, and which set to preload depends on what the token resolves to.
		if (
			(await waitUntilStatsigInitialized()) &&
			expValEquals('access_link_m3', 'isEnabled', true)
		) {
			const content = await resolveAccessLinkContent(match.params.shareToken);

			if (content?.subType === LIVE_PAGE_SUBTYPE) {
				preloadLivePagesComponents();
			} else if (content?.type === 'page') {
				preloadAccessLinkViewPageComponents({
					...match,
					params: {
						...match.params,
						contentId: content.contentId,
						spaceKey: content.spaceKey,
					},
				});
			}
		}
	} else if ((match = matchViewPage(url))) {
		if (shouldPreload) {
			preloadViewPageComponents(match);
		}
	} else if (matchFabricEditor(url)) {
		preloadNativeEditorComponentsPreloader();
	} else if (matchCreateContent(url)) {
		preloadCreateContent();
	} else if (matchWhiteboardPage(url)) {
		preloadWhiteboardPage();
	} else if (matchSearch(url)) {
		preloadSearch();
	}
};
